import { useEffect, useState } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';

import type { i18n } from 'i18next';

import { ThemeProvider } from '@mui/material/styles';

import { cgpvTheme } from '@/ui/style/theme';
import { OverviewMapToggle } from './overview-map-toggle';
import { useStoreAppDisplayLanguage } from '@/core/stores/states/app-state';
import { useStoreMapOverviewMapHideZoom, useStoreMapZoom } from '@/core/stores/states/map-state';
import { logger } from '@/core/utils/logger';
import { Box } from '@/ui/layout';
import { TIMEOUT } from '@/core/utils/constant';
import { useMapController } from '@/core/controllers/use-controllers';

/** The properties for the overview map component. */
export type OverviewMapProps = {
  /** The i18next instance for translations. */
  i18n: i18n;
};

/**
 * Creates an overview map control and adds it to the map.
 *
 * @param props - The overview map properties
 * @returns The overview map container element
 */
export function OverviewMap(props: OverviewMapProps): JSX.Element {
  // Log
  logger.logTraceRender('components/overview-map/overview-map');

  // Props
  const { i18n } = props;

  // Store
  const zoomLevel = useStoreMapZoom();
  const hideOnZoom = useStoreMapOverviewMapHideZoom();
  const displayLanguage = useStoreAppDisplayLanguage();
  const mapController = useMapController();

  // State
  const [isInitialized, setIsInitialized] = useState(false);

  // Values
  // TODO: CHECK - Alternative approach, the determination of 'if the overview-map should be visible' could probably be handled elsewhere and the component only listen to a visibility flag from the store?
  let shouldBeVisible: boolean | undefined = undefined;
  if (isInitialized) {
    shouldBeVisible = hideOnZoom === 0 || zoomLevel > hideOnZoom;
  }

  /**
   * Updates visibility based on zoom level changes.
   */
  useEffect(() => {
    logger.logTraceUseEffect('OVERVIEW-MAP - shouldBeVisible', shouldBeVisible);

    // Tweak the visibility
    mapController.setOverviewMapVisibility(!!shouldBeVisible);
  }, [mapController, shouldBeVisible]);

  /**
   * Initializes the overview map control and renders the toggle button on mount.
   */
  useEffect(() => {
    logger.logTraceUseEffect('OVERVIEW-MAP - mount');

    let root: Root | null = null;
    const toggleButton = document.createElement('div');
    const overviewMapControl = mapController.initOverviewMapControl(toggleButton);

    // Use setTimeout to defer root creation to next tick
    const timeoutId = setTimeout(() => {
      root = createRoot(toggleButton);
      root.render(
        <I18nextProvider i18n={i18n}>
          <ThemeProvider theme={cgpvTheme}>
            <OverviewMapToggle overviewMap={overviewMapControl} />
          </ThemeProvider>
        </I18nextProvider>
      );
      // Store the root reference for cleanup
      mapController.setMapOverviewMapRoot(root);

      // Set initialized to true after everything is set up
      setIsInitialized(true);
    }, TIMEOUT.deferExecution);

    // Cleanup
    return () => {
      logger.logTraceUseEffectUnmount('OVERVIEW-MAP - unmount');
      clearTimeout(timeoutId);
      setTimeout(() => {
        if (root) {
          root.unmount();
          root = null;
        }
      }, TIMEOUT.deferExecution);
      setIsInitialized(false);
    };
  }, [mapController, displayLanguage, i18n]);

  return <Box />;
}
